import { el, clear } from "../dom.js";
import type { ClassificationResult } from "../../engine/engine.types.js";
import type { Experiment, Pattern } from "../../content/types.js";
import { labelFor } from "../../content/evidence-labels.js";

export interface ResultScreenProps {
  result: ClassificationResult;
  primaryPattern: Pattern | null;
  alternativePattern: Pattern | null;
  experiment: Experiment | null;
  onContinue: () => void;
}

/**
 * Los valores de `consistency` son españoles en el MOTOR (`ConsistencyLevel`)
 * y ahí se quedan: son datos, no copy, y renombrarlos tocaría el motor y sus
 * pruebas sin ganar nada. Lo que no puede pasar es que se filtren a la
 * pantalla — y pasaba: la etiqueta se pintaba con `consistency.toUpperCase()`,
 * o sea "ALTA" en una interfaz inglesa. Dos mapas, y ninguno imprime el valor
 * crudo.
 */
const CONSISTENCY_LABEL: Record<ClassificationResult["consistency"], string> = {
  alta: "High consistency",
  moderada: "Moderate consistency",
  abierto: "Open result",
  insuficiente: "Not enough information",
};

const CONSISTENCY_TAG: Record<ClassificationResult["consistency"], string> = {
  alta: "HIGH",
  moderada: "MODERATE",
  abierto: "OPEN",
  insuficiente: "INSUFFICIENT",
};

function block(title: string, content: (Node | string)[]): HTMLElement {
  return el("div", { className: "result-block" }, [
    el("h3", {}, [title]),
    el("div", { className: "content" }, content),
  ]);
}

export function renderResult(container: HTMLElement, props: ResultScreenProps): void {
  clear(container);
  const { result, primaryPattern, alternativePattern, experiment } = props;

  const blocks: HTMLElement[] = [
    block("1 · How to read this", [
      "This is a hypothesis about a pattern, not a definition of who you are. You can reject it, or leave it open.",
    ]),
    (() => {
      const b = block("2 · Main hypothesis", [
        primaryPattern
          ? primaryPattern.shortHypothesis
          : "No single hypothesis is supported well enough by your answers.",
      ]);
      // EL NÚMERO, y debajo. Estuvo ausente hasta el 17 de agosto — no por una
      // decisión, sino porque nadie lo construyó: `number` y `workingName`
      // llevaban desde el principio en el tipo sin que ninguna pantalla los
      // leyera. Alguien lo notó haciendo el test y preguntó si era a propósito.
      //
      // Se enseña porque esconderlo no evita la etiqueta: quien quiera el
      // número lo busca fuera y aterriza en material peor. Y es el puente con
      // lo que la persona ya sabe, y con el libro.
      //
      // Se enseña ASÍ —después de la frase, en pequeño, y nunca en la cabecera—
      // porque el orden es el mensaje: primero qué te pasa, y sólo luego cómo
      // se llama. Al revés sería un veredicto con una explicación detrás.
      //
      // Y SÓLO cuando hay un patrón adjudicado. En `open` hay hipótesis
      // principal pero no ganador, y en `insufficient` no hay nada: poner un
      // número ahí convertiría «no está claro» en un tipo, que es exactamente
      // lo que este motor está construido para no hacer.
      if (primaryPattern && result.state.startsWith("pattern_")) {
        b.querySelector(".content")!.append(
          el("p", { className: "pattern-number" }, [
            `Usually called type ${primaryPattern.number} — ${primaryPattern.workingName}.`,
          ]),
        );
      }
      return b;
    })(),
    (() => {
      const b = block("3 · Consistency", [CONSISTENCY_LABEL[result.consistency]]);
      b.querySelector(".content")!.append(
        el("div", { className: "consistency-tag" }, [CONSISTENCY_TAG[result.consistency]]),
      );
      return b;
    })(),
    block(
      "4 · Evidence",
      result.evidence.length
        ? [el("ul", {}, result.evidence.map((e) => el("li", {}, [labelFor(e)])))]
        : ["Not enough evidence was recorded."],
    ),
    block(
      "5 · Counterevidence",
      result.counterevidence.length
        ? [el("ul", {}, result.counterevidence.map((e) => el("li", {}, [labelFor(e)])))]
        : ["No relevant counterevidence showed up in your answers."],
    ),
    block("6 · Plausible alternative", [
      alternativePattern
        ? `${alternativePattern.shortHypothesis} — one different answer could tip it this way.`
        : "There is no alternative plausible enough to record.",
    ]),
    block("7 · Provisional map", [
      "Situation → signal → provisional underlying interest → habitual behaviour. The full map, in detail, is worked through in Inner Architecture.",
    ]),
    block("8 · The turn", [
      "The point where the strategy itself starts producing, in part, the opposite of what it is trying to protect.",
    ]),
    (() => {
      const b = block("9 · First experiment", [
        experiment
          ? experiment.action
          : "No experiment is offered — the hypothesis isn\u2019t clear enough yet.",
      ]);
      if (experiment) {
        b.querySelector(".content")!.append(
          el("p", {}, [`When: ${experiment.trigger}`]),
          el("p", {}, [`Watch for: ${experiment.observation}`]),
          el("p", { className: "safety-note" }, [experiment.safetyCopy]),
        );
      }
      return b;
    })(),
  ];

  const screen = el("section", { className: "screen" }, [
    el("span", { className: "eyebrow" }, ["Result"]),
    el("h1", {}, ["Your hypothesis."]),
    ...blocks,
    el("div", { className: "hair" }),
    el("button", { className: "btn", type: "button" }, ["Continue →"]),
  ]);

  screen.querySelector("button")!.addEventListener("click", props.onContinue);
  container.append(screen);
}
